/** Lead stage engine UI — staff stage control, agent read-only progress, Ops stage board. */
import { useEffect, useState, useCallback, Fragment } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, AlertTriangle, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";

const slaChip = (l) => {
  if (l.sla_breached) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700" data-testid="sla-breached-chip"><AlertTriangle className="w-3 h-3" /> SLA breached</span>;
  }
  if (!l.sla_due && !l.stage_sla_due) return null;
  const due = new Date(l.sla_due || l.stage_sla_due);
  const hrs = Math.round((due - Date.now()) / 3600000);
  if (Number.isNaN(hrs)) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${hrs <= 8 ? "bg-amber-100 text-amber-800" : "bg-stone-100 text-stone-600"}`}>
      <Clock className="w-3 h-3" /> SLA {hrs > 0 ? `${hrs}h left` : "due"}
    </span>
  );
};

export function StageStepper({ workflow, stage, history = [], terminals = ["Rejected", "Cancelled"] }) {
  const isTerminal = terminals.includes(stage);
  const visited = new Set((history || []).map((h) => h.stage));
  const isCustom = stage && !isTerminal && !workflow.includes(stage);
  return (
    <div className="flex flex-wrap gap-1" data-testid="stage-stepper">
      {workflow.map((s, i) => {
        const cur = s === stage;
        const done = !cur && visited.has(s);
        return (
          <span key={s} title={s}
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${cur ? "bg-emerald-900 text-white border-emerald-900" : done ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-white text-stone-400 border-stone-200"}`}>
            {done && <CheckCircle2 className="w-2.5 h-2.5 inline mr-0.5 -mt-px" />}{i + 1}. {s}
          </span>
        );
      })}
      {isCustom && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-700 text-white border border-violet-700" data-testid="stage-custom-chip">{stage}</span>}
      {isTerminal && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">{stage}</span>}
    </div>
  );
}

/** Agent read-only stage progress (MyLeads expanded row). */
export function LeadStageProgress({ lead }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get(`/leads/${lead.id}/stage-info`).then((r) => setInfo(r.data)).catch(() => {});
  }, [lead.id]);
  if (!info || !info.stage) return null;
  return (
    <div data-testid={`lead-stage-progress-${lead.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-black uppercase text-stone-500">Application stage</div>
        {slaChip(info)}
      </div>
      <StageStepper workflow={info.workflow} stage={info.stage} history={info.history} terminals={info.terminals} />
    </div>
  );
}

const CUSTOM = "__custom__";

/** Staff stage control with history + audit (Admin leads expanded row). */
export default function LeadStageControl({ leadId, onChanged }) {
  const [info, setInfo] = useState(null);
  const [next, setNext] = useState("");
  const [customName, setCustomName] = useState("");
  const [note, setNote] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [audit, setAudit] = useState(null);

  const load = useCallback(() => {
    api.get(`/leads/${leadId}/stage-info`).then((r) => { setInfo(r.data); setNext(r.data.stage || ""); }).catch(() => {});
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    const target = next === CUSTOM ? customName.trim() : next;
    if (!target || target === info?.stage) return;
    try {
      await api.put(`/operations/leads/${leadId}/stage`, { stage: target, note });
      toast.success(`Stage → ${target}`);
      setNote(""); setCustomName("");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const loadAudit = () => {
    if (audit) { setAudit(null); return; }
    api.get(`/leads/${leadId}/audit`).then((r) => setAudit(r.data.items || [])).catch(() => toast.error("Could not load audit trail"));
  };

  if (!info) return null;
  return (
    <div className="space-y-2.5" data-testid={`lead-stage-control-${leadId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs font-black uppercase text-stone-500">Stage</div>
        {slaChip(info)}
        <button onClick={() => setShowHistory((s) => !s)} className="text-[10px] font-bold text-stone-500 underline underline-offset-2">history ({(info.history || []).length})</button>
        <button onClick={loadAudit} className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-700 underline underline-offset-2" data-testid={`lead-audit-toggle-${leadId}`}>
          <History className="w-3 h-3" /> audit trail
        </button>
      </div>
      <StageStepper workflow={info.workflow} stage={info.stage} history={info.history} terminals={info.terminals} />
      <div className="flex items-center gap-2 flex-wrap">
        <select value={next} onChange={(e) => setNext(e.target.value)} data-testid={`lead-stage-select-${leadId}`}
          className="h-8 rounded-full border border-stone-300 bg-white px-3 text-xs font-bold">
          {info.workflow.concat(info.terminals).map((s) => <option key={s} value={s}>{s}</option>)}
          {info.stage && !info.workflow.includes(info.stage) && !info.terminals.includes(info.stage) && (
            <option value={info.stage}>{info.stage}</option>
          )}
          <option value={CUSTOM}>✎ Custom stage…</option>
        </select>
        {next === CUSTOM && (
          <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="Type stage name"
            className="h-8 rounded-full border border-violet-400 px-3 text-xs w-44" data-testid={`lead-stage-custom-${leadId}`} />
        )}
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
          className="h-8 rounded-full border border-stone-300 px-3 text-xs w-52" data-testid={`lead-stage-note-${leadId}`} />
        <Button size="sm" onClick={apply} disabled={next === CUSTOM ? !customName.trim() : (!next || next === info.stage)} data-testid={`lead-stage-apply-${leadId}`}
          className="h-8 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white text-xs font-bold">Update stage</Button>
      </div>
      <p className="text-[10px] text-stone-400">Stages are flexible — any order or a custom stage is fine (e.g. payment can come before the proposal). The list is just the suggested flow.</p>
      {showHistory && (
        <div className="space-y-1 pt-1">
          {[...(info.history || [])].reverse().map((h, i) => (
            <div key={i} className="text-[11px] text-stone-600">
              <span className="font-bold">{h.stage}</span> · {new Date(h.at).toLocaleString()} · {h.by_name || h.by_role}
              {h.note && <span className="italic"> — {h.note}</span>}
            </div>
          ))}
        </div>
      )}
      {audit && (
        <div className="space-y-1 pt-1 border-t border-stone-200" data-testid={`lead-audit-list-${leadId}`}>
          {audit.length === 0 && <div className="text-[11px] text-stone-400 italic">No audit entries yet.</div>}
          {audit.map((a) => (
            <div key={a.id} className="text-[11px] text-stone-600">
              <span className="font-mono text-stone-400">{(a.at || "").slice(0, 16).replace("T", " ")}</span>{" "}
              <span className={`font-bold ${a.by_role === "ai" ? "text-violet-700" : "text-emerald-900"}`}>{a.by_name || a.by_role}</span> — {a.detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Ops → Stages tab: every active lead with inline stage control. */
export function OpsStageBoard() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    api.get("/operations/leads/stages").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto" data-testid="ops-stage-board">
      <div className="flex items-center justify-between px-6 pt-5">
        <div>
          <h3 className="font-display text-lg font-black">Lead Stages</h3>
          <p className="text-xs text-stone-500">Category-specific workflows with 48h SLA per stage. Terminal stages auto-sync the CRM status.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
      </div>
      <table className="w-full text-sm mt-4">
        <thead>
          <tr className="border-b border-stone-200 text-left text-stone-500 text-xs uppercase tracking-wider">
            <th className="py-3 px-6 font-semibold">Lead</th>
            <th className="py-3 px-6 font-semibold">Agent</th>
            <th className="py-3 px-6 font-semibold">Stage</th>
            <th className="py-3 px-6 font-semibold">SLA</th>
            <th className="py-3 px-6 font-semibold"></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-stone-500">No active leads.</td></tr>}
          {items.map((l) => (
            <Fragment key={l.id}>
              <tr className="border-b border-stone-100" data-testid={`stage-row-${l.id}`}>
                <td className="py-3 px-6">
                  <div className="font-bold">{l.customer_name} {l.ref_number && <span className="text-[10px] font-mono text-emerald-800">{l.ref_number}</span>}</div>
                  <div className="text-xs text-stone-500">{l.product_name} · {l.status}</div>
                </td>
                <td className="py-3 px-6 text-xs">
                  <div className="font-semibold">{l.partner_name}</div>
                  <div className="text-stone-500 font-mono">+91 {l.partner_mobile}</div>
                </td>
                <td className="py-3 px-6">
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-900">{l.stage || "—"}</span>
                  <div className="text-[10px] text-stone-400 mt-0.5">{l.workflow?.indexOf(l.stage) >= 0 ? `${l.workflow.indexOf(l.stage) + 1}/${l.workflow.length}` : ""}</div>
                </td>
                <td className="py-3 px-6">{slaChip(l)}</td>
                <td className="py-3 px-6 text-right">
                  <Button size="sm" variant="outline" onClick={() => setOpen(open === l.id ? null : l.id)}
                    data-testid={`stage-manage-${l.id}`} className="h-8 rounded-full text-xs font-bold">
                    {open === l.id ? "Close" : "Manage"}
                  </Button>
                </td>
              </tr>
              {open === l.id && (
                <tr>
                  <td colSpan={5} className="bg-stone-50 border-b border-stone-200 px-6 py-4">
                    <LeadStageControl leadId={l.id} onChanged={load} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
